import { useEffect, useMemo, useState } from "react";
import {
  ActionButton,
  GlassPanel,
  ModalSheet,
  UtilityButton,
} from "@guandan/ui";
import {
  createAnnouncementClient,
  type PublicAnnouncement,
} from "../lib/announcements";
import type { ActiveRoomView } from "../lib/platform-api";
import type { PlayerProfile } from "../lib/storage";
import heroUrl from "../../../../assets/scenes/modern-lounge-haikou-table-empty-v3.webp";
import { PortraitAvatar } from "./PortraitAvatar";

export type LobbyRoute =
  | "lobby"
  | "friends"
  | "records"
  | "rankings"
  | "dress"
  | "settings"
  | "friend-room";

const navItems: readonly {
  route: LobbyRoute;
  label: string;
  icon: string;
  mobileOnly?: boolean;
}[] = [
  { route: "lobby", label: "大厅", icon: "⌂" },
  { route: "friends", label: "好友", icon: "友" },
  { route: "friend-room", label: "好友房", icon: "♧" },
  { route: "records", label: "战绩", icon: "▤" },
  { route: "rankings", label: "排行榜", icon: "榜", mobileOnly: true },
  { route: "dress", label: "头像", icon: "人" },
  { route: "settings", label: "设置", icon: "⚙" },
];

export function BottomNav({
  active,
  onNavigate,
}: {
  active: LobbyRoute;
  onNavigate: (route: LobbyRoute) => void;
}) {
  return (
    <nav className="bottom-nav" aria-label="主要导航">
      {navItems.map((item) => (
        <button
          type="button"
          className={item.mobileOnly ? "mobile-nav-only" : undefined}
          key={item.route}
          aria-current={active === item.route ? "page" : undefined}
          onClick={() => onNavigate(item.route)}
        >
          <span aria-hidden="true">{item.icon}</span>
          {item.label}
        </button>
      ))}
    </nav>
  );
}

export function Lobby({
  profile,
  offline,
  activeRoom,
  activeRoomCheckState,
  onNavigate,
  onQuickMatch,
  onResume,
  onRetryActiveRoom,
}: {
  profile: PlayerProfile;
  offline: boolean;
  activeRoom: ActiveRoomView | null;
  activeRoomCheckState: "idle" | "checking" | "ready" | "failed";
  onNavigate: (route: LobbyRoute) => void;
  onQuickMatch: () => void;
  onResume: () => void;
  onRetryActiveRoom: () => void;
}) {
  const [fairPlayHelp, setFairPlayHelp] = useState(false);
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const announcementClient = useMemo(() => createAnnouncementClient(), []);
  const [announcement, setAnnouncement] = useState<PublicAnnouncement | null>(
    null,
  );
  const networkEntryBlocked =
    offline || activeRoomCheckState !== "ready" || Boolean(activeRoom);
  useEffect(() => {
    if (offline || !announcementClient) return;
    const controller = new AbortController();
    void announcementClient
      .list(controller.signal)
      .then((items) => setAnnouncement(items[0] ?? null))
      .catch(() => undefined);
    return () => controller.abort();
  }, [announcementClient, offline]);
  return (
    <main
      className="lobby-screen"
      style={{ backgroundImage: `url(${heroUrl})` }}
    >
      <div className="lobby-shade" aria-hidden="true" />
      <GlassPanel className="identity-panel">
        <div className="identity-avatar">
          <PortraitAvatar
            config={profile.avatar}
            motion="idle"
            className="identity-avatar-stage"
            label={`${profile.nickname}的现代角色头像`}
          />
        </div>
        <div>
          <h1>{profile.nickname}</h1>
          <p>
            Lv.{profile.level} · 经验 {profile.experience}/5000
          </p>
          <strong>信誉分 {profile.reputation}</strong>
        </div>
      </GlassPanel>
      <div className="lobby-utilities">
        <UtilityButton onClick={() => setFairPlayHelp(true)}>
          语音与公平说明
        </UtilityButton>
        <UtilityButton
          disabled={offline}
          onClick={() => onNavigate("rankings")}
        >
          排行榜
        </UtilityButton>
        <UtilityButton onClick={() => onNavigate("friends")}>
          好友
        </UtilityButton>
        <UtilityButton onClick={() => onNavigate("settings")}>
          设置
        </UtilityButton>
      </div>
      <section
        className={`lobby-actions${activeRoom || activeRoomCheckState === "failed" ? " lobby-actions--with-recovery" : ""}`}
        aria-label="开始游戏"
      >
        {activeRoom ? (
          <ActionButton
            tone="jade"
            size="lg"
            wide
            disabled={offline}
            onClick={onResume}
          >
            <span className="action-icon">↻</span>
            <span>
              <strong>
                {activeRoom.status === "playing" ? "继续牌局" : "返回等待房"}
              </strong>
              <small>
                房号 {activeRoom.code} · {activeRoom.settings.name}
              </small>
            </span>
          </ActionButton>
        ) : null}
        {!activeRoom && activeRoomCheckState === "failed" ? (
          <ActionButton
            tone="jade"
            size="lg"
            wide
            disabled={offline}
            onClick={onRetryActiveRoom}
          >
            <span className="action-icon">↻</span>
            <span>
              <strong>重新检查牌局</strong>
              <small>恢复检查失败，联网入口已安全暂停</small>
            </span>
          </ActionButton>
        ) : null}
        <ActionButton
          tone="orange"
          size="lg"
          wide
          disabled={networkEntryBlocked}
          onClick={onQuickMatch}
        >
          <span className="action-icon">↗</span>
          <span>
            <strong>快速开始</strong>
            <small>
              {offline
                ? "离线不可用"
                : activeRoom
                  ? "请先继续当前牌局"
                  : activeRoomCheckState !== "ready"
                    ? "正在检查未完成牌局"
                    : "真人优先 · 超时 AI 补位"}
            </small>
          </span>
        </ActionButton>
        <ActionButton
          tone="blue"
          size="lg"
          wide
          disabled={networkEntryBlocked}
          onClick={() => onNavigate("friend-room")}
        >
          <span className="action-icon">♧</span>
          <span>
            <strong>好友房</strong>
            <small>
              {offline
                ? "离线不可用"
                : activeRoom
                  ? "请先继续当前房间"
                  : activeRoomCheckState !== "ready"
                    ? "正在检查未完成房间"
                    : "创建、加入或公开招募"}
            </small>
          </span>
        </ActionButton>
      </section>
      <p className="lobby-city-label">
        现代城市会客厅 · {offline ? "离线大厅壳" : "今夜在线"}
      </p>
      {announcement ? (
        <button
          type="button"
          className={`lobby-system-announcement lobby-system-announcement--${announcement.severity}`}
          onClick={() => setAnnouncementOpen(true)}
        >
          <span>系统公告</span>
          <strong>{announcement.title}</strong>
        </button>
      ) : null}
      <BottomNav active="lobby" onNavigate={onNavigate} />
      {fairPlayHelp ? (
        <ModalSheet
          title="语音与公平说明"
          onClose={() => setFairPlayHelp(false)}
        >
          <article className="announcement">
            <h3>全桌交流，隐藏信息隔离</h3>
            <p>
              语音只在当前牌桌全桌广播，默认不录音、不保存；AI
              只接收公开状态和自己的手牌。
            </p>
            <ActionButton tone="jade" onClick={() => setFairPlayHelp(false)}>
              我知道了
            </ActionButton>
          </article>
        </ModalSheet>
      ) : null}
      {announcement && announcementOpen ? (
        <ModalSheet
          title={announcement.title}
          onClose={() => setAnnouncementOpen(false)}
        >
          <article className="announcement">
            <p>{announcement.body}</p>
            <small>
              发布于{" "}
              {new Date(announcement.publishedAt).toLocaleString("zh-CN")}
            </small>
            <ActionButton
              tone="jade"
              onClick={() => setAnnouncementOpen(false)}
            >
              我知道了
            </ActionButton>
          </article>
        </ModalSheet>
      ) : null}
    </main>
  );
}
